import { gql, useMutation, useQuery } from '@apollo/client';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Link } from '@reach/router';
import colors from 'lib/colors';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';
import safeStringLength from 'utils/safeStringLenght';
import useLogout from 'utils/useLogout';
import { ReactComponent as NotificationOFF } from '../../assets/notificationOFF.svg';
import { ReactComponent as NotificationON } from '../../assets/notificationON.svg';
import { meQuery } from '../../graphql/queries';
import { Me } from '../../graphql/types';
import Box from '../Box';
import DropDown from '../DropDown';
import NotificationCenter from '../NotificationCenter';
import ProfileImage from '../ProfileImage';
import StyledArrow from '../StyledArrow';

const UserBanner = () => {
  const [t] = useTranslation();
  const { data, error } = useQuery<Me>(meQuery, {
    fetchPolicy: 'cache-first',
    returnPartialData: true,
  });
  const [setNotificationSound] = useMutation(updateNotificationSound);
  const [menuOpened, setMenuOpened] = useState(false);
  const { loading: logingOut, logout } = useLogout();
  const ref = useRef(null);
  useClickAway(ref, () => menuOpened && toggleMenu());

  const toggleMenu = () => setMenuOpened((prev) => !prev);
  let content;
  if (data != null) {
    if (data.me != null) {
      content = (
        <>
          <ProfileImage size={18} mr={2} />
          {safeStringLength(data.me.name || '')}
          <StyledArrow up={menuOpened} />
        </>
      );
    } else {
      content = t('userBanner.content.notAuthorized', 'Nepřihlášen');
    }
  } else if (error) {
    content = t('userBanner.content.error', 'Chyba');
  } else {
    content = <Box width="6em" height="13px" />;
  }

  const handleNotificationSoundClick = () =>
    setNotificationSound({
      variables: { enabled: !data?.me?.notificationSound },
    });

  const userHasConfirmedNotification =
    data?.me?.hasUnconfirmedNotification || false;
  return (
    <>
      <NotificationCenter
        userHasConfirmedNotification={userHasConfirmedNotification}
      />

      <DropDown
        opened={menuOpened}
        toggleOptions={toggleMenu}
        toggleButtonContent={
          <ContentContainer noLeftPadding={userHasConfirmedNotification}>
            {content}
          </ContentContainer>
        }
      >
        <NavigationButton
          as={Link}
          // @ts-ignore
          to={'/ranking'}
        >
          {t('userBanner.menu.profile', 'Můj profil')}
        </NavigationButton>
        <NavigationButton onClick={handleNotificationSoundClick}>
          {t('userBanner.menu.notificationSound', 'Zvuk notifikace: ')}
          {data?.me?.notificationSound ? (
            <>
              :&nbsp;{t('userBanner.menu.notificationSoundOn', 'ZAP')}&nbsp;
              <NotificationON />
            </>
          ) : (
            <>
              :&nbsp;{t('userBanner.menu.notificationSoundOff', 'VYP')}&nbsp;
              <NotificationOFF css={css({ marginBottom: '-3px' })} />
            </>
          )}
        </NavigationButton>
        <NavigationButton disabled={logingOut} onClick={logout}>
          {t('userBanner.menu.logOut', 'Odhlásit')}
        </NavigationButton>
        <NavigationButton
          as={Link}
          // @ts-ignore
          to={'/first-time-view'}
          css={css({
            textTransform: 'uppercase',
            position: 'relative',
            top: '10px',
            lineHeight: '250%',
            color: colors.infoLinks,
          })}
        >
          {t('userBanner.menu.firstTimeView', 'O hře clashing')}
        </NavigationButton>
      </DropDown>
    </>
  );
};

export default UserBanner;

interface ContentContainerProps {
  noLeftPadding: boolean;
}

const ContentContainer = styled('div')<ContentContainerProps>(
  ({ noLeftPadding }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    backgroundColor: colors.darkBlue,
    color: colors.white,
    fontWeight: 700,
    fontSize: '1em',
    letterSpacing: 0,
    paddingTop: '1em',
    paddingBottom: '1em',
    paddingLeft: noLeftPadding ? 0 : '24px',
    paddingRight: '24px',
    minHeight: '45px',
  }),
);

const NavigationButton = styled('button')({
  outline: 'none',
  textDecoration: 'none',
  color: colors.lobbyOrange,
  fontSize: 'inherit',
  backgroundColor: 'transparent',
  margin: 0,
  padding: 0,
  border: 'none',
});

const updateNotificationSound = gql`
  mutation UpdateNotificationSound($enabled: Boolean!) {
    updateNotificationSound(enabled: $enabled) {
      id
      notificationSound
    }
  }
`;
